import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import Select2 from "../../Components/Accounting/Select2";
import { TableSkeleton } from "../../Components/Accounting/SkeletonLoader";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";

const REPORT_TYPES = {
  "trial-balance": { title: "Trial Balance", icon: "fa-table-list", description: "Summarizes all debit and credit balances to verify ledger accuracy before preparing financial statements." },
  "income-statement": { title: "Income Statement (P&L)", icon: "fa-chart-line", description: "Shows revenues earned and expenses incurred during the period, resulting in net profit or loss." },
  "balance-sheet": { title: "Balance Sheet", icon: "fa-scale-balanced", description: "Snapshot of assets, liabilities, and equity at a point in time. Assets = Liabilities + Equity." },
  "cash-flow": { title: "Cash Flow Statement", icon: "fa-money-bill-transfer", description: "Tracks cash inflows and outflows from operating, investing, and financing activities." },
};

const FinancialReports = () => {
  const { type } = useParams();
  const reportConfig = REPORT_TYPES[type] || REPORT_TYPES["trial-balance"];
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState("");
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split("T")[0]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchFinancialYears(); }, []);
  useEffect(() => { setReportData(null); }, [type]);

  const fetchFinancialYears = async () => {
    const res = await ajaxAccounting.listFinancialYears({});
    if (res.status === "OK") {
      setFinancialYears(res.details || []);
      const cur = (res.details || []).find((y) => y.is_current === 1);
      setSelectedFY(String(cur?.id || res.details?.[0]?.id || ""));
    }
  };

  const fetchReport = async () => {
    if (!selectedFY) { toast.error("Select a financial year."); return; }
    setLoading(true);
    let res;
    const payload = { financial_year_id: parseInt(selectedFY), as_of_date: asOfDate };
    switch (type) {
      case "trial-balance": res = await ajaxAccounting.getTrialBalance(payload); break;
      case "income-statement": res = await ajaxAccounting.getIncomeStatement(payload); break;
      case "balance-sheet": res = await ajaxAccounting.getBalanceSheet(payload); break;
      case "cash-flow": res = await ajaxAccounting.getCashFlow(payload); break;
      default: res = await ajaxAccounting.getTrialBalance(payload);
    }
    if (res.status === "OK") setReportData(res.details);
    else toast.error(res.message || "Failed to generate report");
    setLoading(false);
  };

  const renderTrialBalance = (data) => {
    // The endpoint calls them entries, not accounts.
    const rows = data?.entries || [];

    // Each account's closing balance goes in one column or the other. `balance`
    // is signed in the account's own direction, so a debit-normal account with
    // a negative balance belongs on the credit side, and vice versa.
    const sides = rows.map((r) => {
      const balance = parseFloat(r.balance) || 0;
      const onDebitSide = r.normal_balance === "debit" ? balance >= 0 : balance < 0;
      return { ...r, dr: onDebitSide ? Math.abs(balance) : 0, cr: onDebitSide ? 0 : Math.abs(balance) };
    });
    const totalDr = sides.reduce((s, r) => s + r.dr, 0);
    const totalCr = sides.reduce((s, r) => s + r.cr, 0);
    return (
      <div className="table-responsive">
        <table className="table table-bordered table-sm">
          <thead className="bg-light">
            <tr><th>Code</th><th>Account Name</th><th>Type</th><th className="text-end">Debit (Dr)</th><th className="text-end">Credit (Cr)</th></tr>
          </thead>
          <tbody>
            {sides.map((r, i) => (
              <tr key={i}>
                <td><code>{r.account_code}</code></td>
                <td>{r.account_name}</td>
                <td><span className="badge bg-secondary text-capitalize">{r.type}</span></td>
                <td className="text-end tx-success">{r.dr > 0 ? functions.formatCurrency(r.dr) : "-"}</td>
                <td className="text-end tx-danger">{r.cr > 0 ? functions.formatCurrency(r.cr) : "-"}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-light fw-bold">
            <tr>
              <td colSpan={3}>Totals</td>
              <td className="text-end tx-success">{functions.formatCurrency(totalDr)}</td>
              <td className="text-end tx-danger">{functions.formatCurrency(totalCr)}</td>
            </tr>
            <tr>
              <td colSpan={3}>Difference</td>
              <td colSpan={2} className={`text-end ${Math.abs(totalDr - totalCr) < 0.01 ? "tx-success" : "tx-danger"}`}>
                {Math.abs(totalDr - totalCr) < 0.01 ? "✓ Balanced" : functions.formatCurrency(Math.abs(totalDr - totalCr))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  const renderPL = (data) => {
    const revenue = data?.revenue || [];
    const expenses = data?.expenses || [];
    // Figures live under totals; reading flat keys gave 0 on every report.
    const totalRevenue = data?.totals?.revenue || 0;
    const totalExpenses = (data?.totals?.operating_expenses || 0) + (data?.totals?.other_expenses || 0);
    const netProfit = totalRevenue - totalExpenses;
    const renderSection = (items, color) => items.map((item, i) => (
      <tr key={i}>
        <td style={{ paddingLeft: "24px" }}>
          <code className="tx-11 me-2">{item.account_code}</code>{item.account_name}
        </td>
        <td className="text-end" style={{ color }}>{functions.formatCurrency(item.net_amount)}</td>
      </tr>
    ));
    return (
      <div className="row">
        <div className="col-lg-8 offset-lg-2">
          <table className="table table-bordered">
            <tbody>
              <tr className="bg-light"><td colSpan={2}><strong>Revenue</strong></td></tr>
              {renderSection(revenue, "#10b981")}
              <tr className="fw-bold"><td>Total Revenue</td><td className="text-end tx-success">{functions.formatCurrency(totalRevenue)}</td></tr>
              <tr className="bg-light"><td colSpan={2}><strong>Expenses</strong></td></tr>
              {renderSection(expenses, "#ef4444")}
              <tr className="fw-bold"><td>Total Expenses</td><td className="text-end tx-danger">{functions.formatCurrency(totalExpenses)}</td></tr>
              <tr style={{ background: netProfit >= 0 ? "#d1fae5" : "#fee2e2" }}>
                <td className="fw-bold" style={{ fontSize: "1.05rem" }}>{netProfit >= 0 ? "Net Profit" : "Net Loss"}</td>
                <td className={`text-end fw-bold`} style={{ fontSize: "1.05rem", color: netProfit >= 0 ? "#059669" : "#dc2626" }}>{functions.formatCurrency(Math.abs(netProfit))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  /**
   * Where the cash went. Opening plus the three activities has to arrive at
   * closing, and closing is what the ledger says is in the bank — so the
   * statement is readable as a chain rather than a dump of numbers.
   */
  const renderCashFlow = (data) => {
    const opening = data?.opening_cash || 0;
    const closing = data?.closing_cash || 0;
    const netChange = data?.net_change || 0;

    const sections = [
      { key: "operating", label: "Operating activities", hint: "Day-to-day trading — fees collected, salaries and suppliers paid" },
      { key: "investing", label: "Investing activities", hint: "Buying and selling long-term assets" },
      { key: "financing", label: "Financing activities", hint: "Borrowing, repayments and capital" },
    ];

    return (
      <div className="row">
        <div className="col-lg-9 offset-lg-1">
          <table className="table table-bordered">
            <thead className="bg-light">
              <tr>
                <th>Activity</th>
                <th className="text-end">Cash in</th>
                <th className="text-end">Cash out</th>
                <th className="text-end">Net</th>
              </tr>
            </thead>
            <tbody>
              <tr className="fw-semibold">
                <td colSpan={3}>Cash at the start of the period</td>
                <td className="text-end">{functions.formatCurrency(opening)}</td>
              </tr>

              {sections.map(({ key, label, hint }) => {
                const s = data?.[key] || { inflow: 0, outflow: 0, net: 0 };
                return (
                  <tr key={key}>
                    <td>
                      <div className="fw-semibold">{label}</div>
                      <div className="tx-11 tx-muted">{hint}</div>
                    </td>
                    <td className="text-end tx-success">
                      {s.inflow ? functions.formatCurrency(s.inflow) : "-"}
                    </td>
                    <td className="text-end tx-danger">
                      {s.outflow ? functions.formatCurrency(s.outflow) : "-"}
                    </td>
                    <td className={`text-end fw-semibold ${s.net < 0 ? "tx-danger" : ""}`}>
                      {functions.formatCurrency(s.net)}
                    </td>
                  </tr>
                );
              })}

              <tr className="fw-bold bg-light">
                <td colSpan={3}>{netChange >= 0 ? "Increase in cash" : "Decrease in cash"}</td>
                <td className={`text-end ${netChange < 0 ? "tx-danger" : "tx-success"}`}>
                  {functions.formatCurrency(netChange)}
                </td>
              </tr>
              <tr style={{ background: "#eef2ff" }}>
                <td colSpan={3} className="fw-bold" style={{ fontSize: "1.05rem" }}>
                  Cash at the end of the period
                </td>
                <td className="text-end fw-bold tx-primary" style={{ fontSize: "1.05rem" }}>
                  {functions.formatCurrency(closing)}
                </td>
              </tr>
            </tbody>
          </table>

          <p className="tx-12 tx-muted">
            Every movement through a cash or bank account, grouped by what the money was
            for. The closing figure is what the ledger says those accounts hold.
          </p>
        </div>
      </div>
    );
  };

  const renderBalanceSheet = (data) => {
    const assets = data?.assets || [];
    const liabilities = data?.liabilities || [];
    const equity = data?.equity || [];
    const totalAssets = data?.totals?.total_assets || 0;
    const totalLiabilities = data?.totals?.total_liabilities || 0;
    // Equity has to include this year profit, or the sheet will not balance.
    const totalEquity = data?.totals?.total_equity_inc_pl ?? data?.totals?.total_equity ?? 0;
    const renderRows = (items, color) => items.map((item, i) => (
      <tr key={i}>
        <td style={{ paddingLeft: "24px" }}>
          <code className="tx-11 me-2">{item.account_code}</code>{item.account_name}
        </td>
        <td className="text-end" style={{ color }}>{functions.formatCurrency(item.net_amount)}</td>
      </tr>
    ));
    return (
      <div className="row">
        <div className="col-lg-6">
          <table className="table table-bordered mb-3">
            <tbody>
              <tr className="bg-primary text-white"><td colSpan={2}><strong>Assets</strong></td></tr>
              {renderRows(assets, "#6366f1")}
              <tr className="fw-bold bg-light"><td>Total Assets</td><td className="text-end">{functions.formatCurrency(totalAssets)}</td></tr>
            </tbody>
          </table>
        </div>
        <div className="col-lg-6">
          <table className="table table-bordered mb-3">
            <tbody>
              <tr className="bg-danger text-white"><td colSpan={2}><strong>Liabilities</strong></td></tr>
              {renderRows(liabilities, "#ef4444")}
              <tr className="fw-bold bg-light"><td>Total Liabilities</td><td className="text-end">{functions.formatCurrency(totalLiabilities)}</td></tr>
              <tr className="bg-warning text-dark"><td colSpan={2}><strong>Equity</strong></td></tr>
              {renderRows(equity, "#d97706")}
              <tr className="fw-bold bg-light"><td>Total Equity</td><td className="text-end">{functions.formatCurrency(totalEquity)}</td></tr>
              <tr style={{ background: "#dbeafe" }}><td className="fw-bold">Liabilities + Equity</td><td className="text-end fw-bold">{functions.formatCurrency(totalLiabilities + totalEquity)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderReport = () => {
    if (loading) return <TableSkeleton rows={10} cols={3} />;
    if (!reportData) return <div className="text-center py-5 tx-muted"><i className="fa-solid fa-chart-bar" style={{ fontSize: "3rem", opacity: 0.2 }}></i><p className="mt-3">Click "Generate Report" to view results.</p></div>;
    switch (type) {
      case "trial-balance": return renderTrialBalance(reportData);
      case "income-statement": return renderPL(reportData);
      case "balance-sheet": return renderBalanceSheet(reportData);
      case "cash-flow": return renderCashFlow(reportData);
      default: return renderTrialBalance(reportData);
    }
  };

  return (
    <AppContainer
      title={reportConfig.title}
      previousPageTitle="Reports"
      description={reportConfig.description}
      actions={[
        { label: "Generate Report", icon: "fa-solid fa-play", className: "btn btn-primary", onClick: fetchReport },
        { label: "Print", icon: "fa-solid fa-print", className: "btn btn-outline-secondary", onClick: () => window.print() },
      ]}
    >
      <Toaster />
      <div className="card custom-card p-3 mb-3">
        <div className="row g-2">
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Financial Year</label>
            <Select2 options={financialYears.map((y) => ({ value: String(y.id), label: y.name }))} value={selectedFY} onChange={setSelectedFY} placeholder="Select Year" />
          </div>
          <div className="col-lg-3">
            <label className="form-label tx-12 fw-semibold">As of Date</label>
            <input type="date" className="form-control" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} />
          </div>
          <div className="col-lg-5 d-flex align-items-end gap-2">
            {Object.entries(REPORT_TYPES).map(([key, cfg]) => (
              <Link key={key} to={`/accounting/reports/${key}`} className={`btn btn-sm ${type === key ? "btn-primary" : "btn-outline-secondary"}`}>
                <i className={`fa-solid ${cfg.icon} me-1`}></i>{cfg.title.split("(")[0].trim()}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="card custom-card p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-semibold mb-0">{reportConfig.title}</h6>
          {reportData && <span className="tx-12 tx-muted">As of {functions.formatDate(asOfDate)}</span>}
        </div>
        {renderReport()}
      </div>
    </AppContainer>
  );
};

export default FinancialReports;
